/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable indent */
import {
  Button as MaterialButton,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InputIcon from '@material-ui/icons/Input';
import clsx from 'clsx';
import ButtonExport from 'components/ButtonExport';
import PageTitle from 'components/PageTitle';
import SearchFilterBar from 'components/SearchFilterBar';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import FormClonePartner from 'containers/Partners/ListPartners/FormClonePartner';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  exportPartnerSummaryRequest,
  getAllPartnersOperatorRequest,
} from 'modules/partners/actions';
import {
  AllPartnersFields,
  ListAllPartnersDataProps,
} from 'modules/partners/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { getCompanyName } from 'utils/tsHelpers';
import { filterOptions } from './filterOptions';
import PartnersDashboardCards from './PartnersDashboardCards';
import styles from './styles';
import { getHeaders } from './tableHeaders';

const useStyles = makeStyles(styles);

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'create_new_partner',
  'edit_partner_details',
  'edit_user_theme_config',
  'list_partner_servers',
  'list_partner_instances',
];

interface Props {
  isLoading: boolean;
  allPartnersList: ListAllPartnersDataProps;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  getAllPartnersOperatorRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
  exportPartnerSummaryRequest: (query) => void;
}

const AllPartnersList = ({
  isLoading,
  allPartnersList,
  role,
  companySelected,
  permissions,
  getAllPartnersOperatorRequest,
  modalOpen,
  selectCompany,
  selectPartner,
  exportPartnerSummaryRequest,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
  } = useTableFilters();

  const [showTable, setShowTable] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const history = useHistory();

  const getPartners = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;

      getAllPartnersOperatorRequest({
        ...query,
      });

      setRefresh(refresh);
    },
    [getAllPartnersOperatorRequest, query, showTable]
  );

  React.useEffect(() => {
    getPartners();
  }, [getPartners]);

  const handleClick = (item: AllPartnersFields, toUrl: string) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      name: item?.partner_name,
      shortName: item?.partner_name,
      id: item?.partner_uuid,
    });
    history.push(toUrl);
  };

  const openFormClonePartner = (partner: AllPartnersFields) => {
    modalOpen({
      title: i18n.t('PARTNER_PAGE.CLONE_PARTNER_KEY'),
      content: <FormClonePartner partner={partner} query={query} />,
      noBorder: true,
      maxWidth: 'md',
    });
  };

  const handleToContext = item =>
    handleClick(item, `/partners/${item?.partner_uuid}/configuration`);

  const getHeadersPDF = permissions => [
    getHeaders(permissions)
      .filter(header => header.key !== '')
      .map(header => i18n.t(header.i18n)),
  ];

  const getPartnersListPDF: Array<[
    string,
    string,
    string,
    boolean,
    number,
    number,
    number,
    string
  ]> = allPartnersList?.results.map(item => [
    item?.partner_name,
    item?.company_name,
    item?.partner_alias,
    item?.partner_new_front_enabled,
    item?.num_environments,
    item?.num_clients,
    item?.num_users,
    String(formatDateValue(item?.user_last_access)),
  ]);

  const getCSV = () => {
    exportPartnerSummaryRequest({
      query,
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('PARTNER_PAGE.LIST_PARTNER_KEY')}
          subtitle={i18n.t('PARTNER_PAGE.LIST_PARTNER_AVAILABLE_KEY')}
        />

        <Grid item className={classes.actionsContainer}>
          <Permission value="create_new_partner">
            <MaterialButton
              id="new-partner"
              variant="contained"
              color="secondary"
              size="large"
              className={clsx({
                [classes.addButtonGreen]: inAdminView(role, companySelected),
                [classes.addButtonGray]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [classes.addButtonBlue]: inOperatorView(role, companySelected),
                [classes.addButtonYellow]: companySelected,
              })}
              startIcon={<AddIcon className={classes.addButtonIcon} />}
              component={Link}
              to="/partners/new"
            >
              {i18n.t('PARTNER_KEY')}
            </MaterialButton>
          </Permission>
          {!!permissions && permissions?.can_export_partner_summary_list && (
            <ButtonExport
              context={i18n.t('PARTNERS_KEY')}
              getHeadersPDF={getHeadersPDF(permissions)}
              getBodyListPDF={getPartnersListPDF}
              csvAction={getCSV}
              filters={query.filters}
              filterOptions={filterOptions}
            />
          )}
        </Grid>
      </Grid>

      <PartnersDashboardCards
        filters={query.filters}
        onApplyFilter={() => setShowTable(true)}
        setQuery={setQuery}
      />

      <Divider className={classes.divider} />

      <Grid container className={classes.searchContainer}>
        <SearchFilterBar
          filterOptions={filterOptions}
          isLoading={isLoading}
          searchValue={searchValue}
          query={query}
          onApplyFilters={() => setShowTable(true)}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
          filteredItemsCounterProps={{
            countFilteredItems: allPartnersList?.count,
            countTotalItems: allPartnersList?.total_count,
            isLoading,
            totalItemsText: i18n.t('PARTNERS_TOTAL_KEY'),
          }}
        />

        {showTable && (
          <TableItems
            header={
              <>
                {getHeaders(permissions).map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    textAlignCenter={item?.textAlignCenter}
                    key={item?.key}
                    markOderItem={itemOrder === item?.key}
                    order={item?.order}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: AllPartnersFields, index) => (
              <TableRow className={classes.tableRow} key={item?.partner_uuid}>
                <TableCell
                  maxLength={25}
                  ellipsis
                  centerTitle
                  className={classes.centerFields}
                  onClick={() => handleToContext(item)}
                >
                  <IconButton
                    id={`access-partner-${index}`}
                    onClick={() => handleToContext(item)}
                  >
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
                <TableCell
                  maxLength={30}
                  ellipsis
                  className={clsx({
                    [classes.textLong]: item?.partner_name,
                  })}
                  onClick={() => handleToContext(item)}
                >
                  {item?.partner_name}
                </TableCell>
                <TableCell
                  maxLength={25}
                  ellipsis
                  className={clsx({
                    [classes.textLong]: item?.company_name,
                  })}
                  onClick={() => handleToContext(item)}
                >
                  {item?.company_name}
                </TableCell>
                <TableCell onClick={() => handleToContext(item)}>
                  {item?.partner_alias || 'N/A'}
                </TableCell>

                <TableCell
                  className={classes.centerFields}
                  onClick={() => handleToContext(item)}
                >
                  <Grid container justify="center">
                    {item?.partner_new_front_enabled
                      ? i18n.t('YES_KEY')
                      : i18n.t('NO_KEY')}
                  </Grid>
                </TableCell>
                <TableCell onClick={() => handleToContext(item)} align="center">
                  {item?.num_environments}
                </TableCell>
                <TableCell onClick={() => handleToContext(item)} align="center">
                  {item?.num_clients}
                </TableCell>
                <TableCell onClick={() => handleToContext(item)} align="center">
                  {item?.num_users}
                </TableCell>
                <TableCell
                  centerTitle
                  textAlignCenter
                  className={classes.dateField}
                  onClick={() => handleToContext(item)}
                >
                  {formatDateValue(item?.user_last_access)}
                </TableCell>
                <Permission value={requiredActionsPermissions}>
                  <TableCell className={classes.centerFields}>
                    <MenuActions id={`btn-options-partners-${index}`}>
                      <Permission value="edit_partner_details">
                        <MenuActionItem
                          id="btn-partner-configuration"
                          component={Link}
                          onClick={() => {
                            handleClick(
                              item,
                              `partners/${item?.partner_uuid}/configuration`
                            );
                          }}
                        >
                          <Typography className={classes.menuOptions}>
                            {i18n.t('CONFIG_KEY')}
                          </Typography>
                        </MenuActionItem>
                      </Permission>
                      <Permission value="list_partner_servers">
                        <MenuActionItem
                          id="btn-partner-servers"
                          component={Link}
                          onClick={() => {
                            handleClick(
                              item,
                              `partners/${item?.partner_uuid}/servers`
                            );
                          }}
                        >
                          <Typography className={classes.menuOptions}>
                            {i18n.t('SERVERS_KEY')}
                          </Typography>
                        </MenuActionItem>
                      </Permission>
                      {item?.show_instances_list && (
                        <Permission value="list_partner_instances">
                          <MenuActionItem
                            id="btn-instances"
                            component={Link}
                            to={`partners/${item?.partner_uuid}/instances`}
                          >
                            <Typography className={classes.menuOptions}>
                              {i18n.t('INSTANCES_KEY')}
                            </Typography>
                          </MenuActionItem>
                        </Permission>
                      )}
                      <Permission value="edit_user_theme_config">
                        <MenuActionItem
                          id="btn-custom-front"
                          component={Link}
                          onClick={() => {
                            handleClick(
                              item,
                              `partners/${item?.partner_uuid}/custom-front`
                            );
                          }}
                        >
                          <Typography className={classes.menuOptions}>
                            {i18n.t('GROUPS_PAGE.FRONTEND_CUSTOMIZATION_KEY')}
                          </Typography>
                        </MenuActionItem>
                      </Permission>
                      <Permission value="create_new_partner">
                        <MenuActionItem
                          id="btn-clone-partner"
                          onClick={() => openFormClonePartner(item)}
                        >
                          <Typography className={classes.menuOptions}>
                            {i18n.t('CLONE_KEY')}
                          </Typography>
                        </MenuActionItem>
                      </Permission>
                    </MenuActions>
                  </TableCell>
                </Permission>
              </TableRow>
            )}
            data={allPartnersList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('PARTNER_PAGE.NONE_PARTNER_KEY')}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  partners: {
    isLoading,
    allPartnersList,
    isLoadingLDAPTypeReport,
    isLoadingNewFrontReport,
    isLoadingProviderTypeReport,
    isLoadingSSOReport,
  },
}: ApplicationState) => ({
  isLoading:
    isLoading ||
    isLoadingLDAPTypeReport ||
    isLoadingNewFrontReport ||
    isLoadingProviderTypeReport ||
    isLoadingSSOReport,
  allPartnersList,
  companySelected,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getAllPartnersOperatorRequest,
  modalOpen,
  selectCompany,
  selectPartner,
  exportPartnerSummaryRequest,
})(AllPartnersList);
